import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import STRINGS from "../../Constants/Strings";
import options, { setOptions } from "Utils/EmploymentAndIncomeUtilities";

export const EditTaxationModal = ({
  showTaxationEditModal,
  handleTaxationModalClose,
  applicant,
  handleTaxationSaveClick,
  finInfo = {},
}) => {
  const [taxationFormData, setTaxationFormData] = useState({
    areYouTaxResidentOfCanada: true,
    areYouTaxResidentOfUsa: false,
    areYouTaxResidentOfOtherCountry: false,
    sin: "",
    ssnUsa: "",
    ssn: "",
    countryName: "",
    countryList: "",
    countryOption: [],
    showOtherTaxResidencyField: false,
  });

  const [taxationFormErrors, setTaxationFormErrors] = useState({
    sinSsnNoneErrorList: "",
    sinErrorList: "",
    ssnUsaErrorList: "",
    ssnErrorList: "",
    countryNameErrorList: "",
    countryListErrorList: "",
  });

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!applicant || !showTaxationEditModal) {
      hasInitialized.current = false;
      return;
    }

    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const { regulatory, member } = applicant;

    const resOptions = setOptions(
      regulatory?.taxResidency || finInfo.defaultTaxResidency,
      finInfo
    );

    let initialState = {
      sin: member?.sin || "",
      ssnUsa: regulatory?.ssn || "",
      areYouTaxResidentOfUsa: !!regulatory?.ssn,
      areYouTaxResidentOfCanada: !!member?.sin,
      areYouTaxResidentOfOtherCountry: false,
      ssn: "",
      countryName: "",
      countryList: "",
      countryOption: [],
      showOtherTaxResidencyField: false,
    };

    if (finInfo.useRegulatoryFieldsModel) {
      const oecdTaxResidency = regulatory?.regulatoryFields?.find(
        (reg) => reg.Code === 3
      );
      const oecdTaxId = regulatory?.regulatoryFields?.find(
        (reg) => reg.Code === 2
      );

      if (oecdTaxResidency?.Value) {
        initialState.areYouTaxResidentOfOtherCountry = true;
        initialState.ssn = oecdTaxId?.Value || "";
        initialState.countryName = oecdTaxResidency?.Value || "";
      }
    } else {
      if (resOptions) {
        initialState.areYouTaxResidentOfCanada = resOptions.taxResidentHome;
        initialState.areYouTaxResidentOfOtherCountry =
          resOptions.taxResidentOther;

        const selectedOption = resOptions.options?.find(
          (obj) =>
            obj.taxResidencyResult.taxResidency ===
            (regulatory?.taxResidency || finInfo.defaultTaxResidency)
        );

        if (selectedOption) {
          initialState.showOtherTaxResidencyField =
            selectedOption.taxResidencyResult.showOtherTaxResidencyField;
          initialState.countryList = regulatory?.taxResidencyOther || "";
          initialState.countryName = selectedOption.taxResidentOtherName || "";
        }
      }
    }

    if (typeof options === "function" && finInfo) {
      const generatedOptions = options(
        initialState.areYouTaxResidentOfCanada,
        initialState.areYouTaxResidentOfOtherCountry,
        finInfo
      );
      initialState.countryOption = generatedOptions || [];
    }

    setTaxationFormData(initialState);
  }, [showTaxationEditModal, applicant, finInfo]);

  const handleChoice = (name, choice) => {
    const newAreYouTaxResidentOfCanada =
      name === "areYouTaxResidentOfCanada"
        ? choice
        : taxationFormData.areYouTaxResidentOfCanada;
    const newAreYouTaxResidentOfOtherCountry =
      name === "areYouTaxResidentOfOtherCountry"
        ? choice
        : taxationFormData.areYouTaxResidentOfOtherCountry;

    let newOptions = taxationFormData.countryOption;
    if (typeof options === "function" && finInfo) {
      newOptions =
        options(
          newAreYouTaxResidentOfCanada,
          newAreYouTaxResidentOfOtherCountry,
          finInfo
        ) || [];
    }

    setTaxationFormData((prev) => {
      const newData = { ...prev, [name]: choice, countryOption: newOptions };

      if (name === "areYouTaxResidentOfCanada" && !choice) newData.sin = "";
      if (name === "areYouTaxResidentOfUsa" && !choice) newData.ssnUsa = "";
      if (name === "areYouTaxResidentOfOtherCountry" && !choice) {
        newData.countryName = "";
        newData.ssn = "";
        newData.countryList = "";
      }

      return newData;
    });

    setTaxationFormErrors((prev) => ({
      ...prev,
      sinSsnNoneErrorList: "",
      sinErrorList:
        name === "areYouTaxResidentOfCanada" ? "" : prev.sinErrorList,
      ssnUsaErrorList:
        name === "areYouTaxResidentOfUsa" ? "" : prev.ssnUsaErrorList,
      ssnErrorList:
        name === "areYouTaxResidentOfOtherCountry" ? "" : prev.ssnErrorList,
      countryNameErrorList:
        name === "areYouTaxResidentOfOtherCountry"
          ? ""
          : prev.countryNameErrorList,
      countryListErrorList:
        name === "areYouTaxResidentOfOtherCountry"
          ? ""
          : prev.countryListErrorList,
    }));
  };

  const formatSIN = (value) => {
    if (!value) return "";
    const cleaned = value.replace(/[^0-9]/g, "");
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
      6,
      9
    )}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "sin" || name === "ssnUsa" || name === "ssn") {
      const cleanedValue = value ? value.replace(/[^0-9a-zA-Z]+/gi, "") : "";
      const limitedValue =
        finInfo.useRegulatoryFieldsModel && name === "ssn"
          ? cleanedValue.slice(0, 24)
          : cleanedValue.slice(0, 9);

      setTaxationFormData((prev) => ({ ...prev, [name]: limitedValue }));
    } else {
      setTaxationFormData((prev) => ({ ...prev, [name]: value }));
    }

    setTaxationFormErrors((prev) => ({
      ...prev,
      [`${name}ErrorList`]: "",
      sinSsnNoneErrorList: "",
    }));

    if (name === "countryName" && value) {
      const selectedOption = taxationFormData.countryOption?.find(
        (obj) => obj.taxResidentOtherName === value
      );
      if (selectedOption?.taxResidencyResult) {
        setTaxationFormData((prev) => ({
          ...prev,
          showOtherTaxResidencyField:
            selectedOption.taxResidencyResult.showOtherTaxResidencyField ||
            false,
        }));
      }
    }
  };

  const validateForm = () => {
    const errors = {
      sinSsnNoneErrorList: "",
      sinErrorList: "",
      ssnUsaErrorList: "",
      ssnErrorList: "",
      countryNameErrorList: "",
      countryListErrorList: "",
    };

    if (
      !taxationFormData.areYouTaxResidentOfCanada &&
      !taxationFormData.areYouTaxResidentOfUsa &&
      !taxationFormData.areYouTaxResidentOfOtherCountry
    ) {
      errors.sinSsnNoneErrorList =
        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_TAXATION_DETAILS_MODAL.ERROR_NO_RESIDENCY;
    }

    if (taxationFormData.areYouTaxResidentOfCanada) {
      if (!taxationFormData.sin || taxationFormData.sin.trim() === "") {
        errors.sinErrorList =
          STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_TAXATION_DETAILS_MODAL.ERROR_SIN_REQUIRED;
      } else if (taxationFormData.sin.length !== 9) {
        errors.sinErrorList =
          STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_TAXATION_DETAILS_MODAL.ERROR_SIN_INVALID;
      }
    }

    if (taxationFormData.areYouTaxResidentOfUsa) {
      if (!taxationFormData.ssnUsa || taxationFormData.ssnUsa.trim() === "") {
        errors.ssnUsaErrorList =
          STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_TAXATION_DETAILS_MODAL.ERROR_SSN_USA_REQUIRED;
      } else if (taxationFormData.ssnUsa.length !== 9) {
        errors.ssnUsaErrorList =
          STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_TAXATION_DETAILS_MODAL.ERROR_SSN_USA_INVALID;
      }
    }

    if (taxationFormData.areYouTaxResidentOfOtherCountry) {
      if (
        !taxationFormData.countryName ||
        taxationFormData.countryName.trim() === ""
      ) {
        errors.countryNameErrorList =
          STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_TAXATION_DETAILS_MODAL.ERROR_COUNTRY_REQUIRED;
      }
      if (!taxationFormData.ssn || taxationFormData.ssn.trim() === "") {
        errors.ssnErrorList =
          STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_TAXATION_DETAILS_MODAL.ERROR_TAX_ID_REQUIRED;
      }
      if (taxationFormData.showOtherTaxResidencyField) {
        if (
          !taxationFormData.countryList ||
          taxationFormData.countryList.trim() === ""
        ) {
          errors.countryListErrorList =
            STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL.EDIT_TAXATION_DETAILS_MODAL.ERROR_COUNTRY_LIST_REQUIRED;
        }
      }
    }

    setTaxationFormErrors(errors);
    return !Object.values(errors).some((error) => error !== "");
  };

  const handleSave = () => {
    if (validateForm()) {
      handleTaxationSaveClick(taxationFormData);
      handleTaxationModalClose();
    }
  };

  return (
    <Modal
      show={showTaxationEditModal}
      onHide={handleTaxationModalClose}
      className="[ popup-modal__container ]"
      aria-labelledby="taxation-modal-title"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="[ popup-modal__header ]" closeButton>
        <Modal.Title id="taxation-modal-title">
          {
            STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
              .EDIT_TAXATION_DETAILS_MODAL.TITLE
          }
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="[ row ]">
          <div className="[ col-12 ]">
            {taxationFormErrors.sinSsnNoneErrorList && (
              <div className="[ alert alert-danger mb-3 ]" role="alert">
                {taxationFormErrors.sinSsnNoneErrorList}
              </div>
            )}
            <div className="[ form-container ]">
              {/* Canada */}
              <div className="[ mb-3 ]">
                <div className="[ form-label ]">
                  {
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_TAXATION_DETAILS_MODAL.CANADA_RESIDENT_LABEL
                  }
                </div>
                <div className="[ row ] [ custom-radios ]">
                  <div className="[ col-6 ]">
                    <button
                      type="button"
                      className={
                        taxationFormData.areYouTaxResidentOfCanada
                          ? "[ btn custom-radio-selected ]"
                          : "[ btn custom-radio-unselected ]"
                      }
                      onClick={() =>
                        handleChoice("areYouTaxResidentOfCanada", true)
                      }
                    >
                      {
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_TAXATION_DETAILS_MODAL.YES
                      }
                    </button>
                  </div>
                  <div className="[ col-6 ]">
                    <button
                      type="button"
                      className={
                        !taxationFormData.areYouTaxResidentOfCanada
                          ? "[ btn custom-radio-selected ]"
                          : "[ btn custom-radio-unselected ]"
                      }
                      onClick={() =>
                        handleChoice("areYouTaxResidentOfCanada", false)
                      }
                    >
                      {
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_TAXATION_DETAILS_MODAL.NO
                      }
                    </button>
                  </div>
                </div>
              </div>

              {taxationFormData.areYouTaxResidentOfCanada && (
                <div className="[ drawer-content-container ]">
                  <div className="[ info-title ]">
                    {STRINGS.TAX_INFO.FORM.TAX_RESIDENCY}
                  </div>
                  <div className="[ mb-3 ]">
                    <label htmlFor="sin" className="[ form-label ]">
                      {
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_TAXATION_DETAILS_MODAL.SIN_LABEL
                      }
                    </label>
                    <input
                      id="sin"
                      type="text"
                      name="sin"
                      className={`[ form-control ] ${
                        taxationFormErrors.sinErrorList ? "[ is-invalid ]" : ""
                      }`}
                      value={formatSIN(taxationFormData.sin)}
                      onChange={handleInputChange}
                      placeholder={
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_TAXATION_DETAILS_MODAL.SIN_PLACEHOLDER
                      }
                      maxLength={11}
                    />
                    {taxationFormErrors.sinErrorList && (
                      <div className="[ invalid-feedback ] [ error-msg ]">
                        {taxationFormErrors.sinErrorList}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {finInfo.useRegulatoryFieldsModel && (
                <>
                  <div className="[ mb-3 ]">
                    <div className="[ form-label ]">
                      {
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_TAXATION_DETAILS_MODAL.USA_RESIDENT_LABEL
                      }
                    </div>
                    <div className="[ row ] [ custom-radios ]">
                      <div className="[ col-6 ]">
                        <button
                          type="button"
                          className={
                            taxationFormData.areYouTaxResidentOfUsa
                              ? "[ btn custom-radio-selected ]"
                              : "[ btn custom-radio-unselected ]"
                          }
                          onClick={() =>
                            handleChoice("areYouTaxResidentOfUsa", true)
                          }
                        >
                          {
                            STRINGS.BRANCH.MEMBER_PROFILE
                              .MEMBER_QUICK_VIEW_PANEL
                              .EDIT_TAXATION_DETAILS_MODAL.YES
                          }
                        </button>
                      </div>
                      <div className="[ col-6 ]">
                        <button
                          type="button"
                          className={
                            !taxationFormData.areYouTaxResidentOfUsa
                              ? "[ btn custom-radio-selected ]"
                              : "[ btn custom-radio-unselected ]"
                          }
                          onClick={() =>
                            handleChoice("areYouTaxResidentOfUsa", false)
                          }
                        >
                          {
                            STRINGS.BRANCH.MEMBER_PROFILE
                              .MEMBER_QUICK_VIEW_PANEL
                              .EDIT_TAXATION_DETAILS_MODAL.NO
                          }
                        </button>
                      </div>
                    </div>
                  </div>

                  {taxationFormData.areYouTaxResidentOfUsa && (
                    <div className="[ drawer-content-container ]">
                      <div className="[ info-title ]">
                        {STRINGS.TAX_INFO.FORM.USA_TAX_RESIDENCY}
                      </div>
                      <div className="[ mb-3 ]">
                        <label htmlFor="ssnUsa" className="[ form-label ]">
                          {
                            STRINGS.BRANCH.MEMBER_PROFILE
                              .MEMBER_QUICK_VIEW_PANEL
                              .EDIT_TAXATION_DETAILS_MODAL.SSN_USA_LABEL
                          }
                        </label>
                        <input
                          id="ssnUsa"
                          type="text"
                          name="ssnUsa"
                          className={`[ form-control ] ${
                            taxationFormErrors.ssnUsaErrorList
                              ? "[ is-invalid ]"
                              : ""
                          }`}
                          value={taxationFormData.ssnUsa}
                          onChange={handleInputChange}
                          placeholder={
                            STRINGS.BRANCH.MEMBER_PROFILE
                              .MEMBER_QUICK_VIEW_PANEL
                              .EDIT_TAXATION_DETAILS_MODAL.SSN_USA_PLACEHOLDER
                          }
                          maxLength={9}
                        />
                        {taxationFormErrors.ssnUsaErrorList && (
                          <div className="[ invalid-feedback ] [ error-msg ]">
                            {taxationFormErrors.ssnUsaErrorList}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="[ mb-3 ]">
                <div className="[ form-label ]">
                  {
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_TAXATION_DETAILS_MODAL.OTHER_COUNTRY_RESIDENT_LABEL
                  }
                </div>
                <div className="[ row ] [ custom-radios ]">
                  <div className="[ col-6 ]">
                    <button
                      type="button"
                      className={
                        taxationFormData.areYouTaxResidentOfOtherCountry
                          ? "[ btn custom-radio-selected ]"
                          : "[ btn custom-radio-unselected ]"
                      }
                      onClick={() =>
                        handleChoice("areYouTaxResidentOfOtherCountry", true)
                      }
                    >
                      {
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_TAXATION_DETAILS_MODAL.YES
                      }
                    </button>
                  </div>
                  <div className="[ col-6 ]">
                    <button
                      type="button"
                      className={
                        !taxationFormData.areYouTaxResidentOfOtherCountry
                          ? "[ btn custom-radio-selected ]"
                          : "[ btn custom-radio-unselected ]"
                      }
                      onClick={() =>
                        handleChoice("areYouTaxResidentOfOtherCountry", false)
                      }
                    >
                      {
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_TAXATION_DETAILS_MODAL.NO
                      }
                    </button>
                  </div>
                </div>
              </div>

              {taxationFormData.areYouTaxResidentOfOtherCountry && (
                <>
                  <div className="[ drawer-content-container ]">
                    <div className="[ info-title ]">
                      {STRINGS.TAX_INFO.FORM.FOREIGN_TAX_RESIDENCY}
                    </div>
                    <div className="[ mb-3 ]">
                      <label htmlFor="countryName" className="[ form-label ]">
                        {
                          STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                            .EDIT_TAXATION_DETAILS_MODAL.COUNTRY_LABEL
                        }
                      </label>
                      <select
                        id="countryName"
                        name="countryName"
                        className={`[ form-control ] [ select__dropdown ] ${
                          taxationFormErrors.countryNameErrorList
                            ? "[ is-invalid ]"
                            : ""
                        }`}
                        value={taxationFormData.countryName}
                        onChange={handleInputChange}
                      >
                        <option value="">
                          {
                            STRINGS.BRANCH.MEMBER_PROFILE
                              .MEMBER_QUICK_VIEW_PANEL
                              .EDIT_TAXATION_DETAILS_MODAL.SELECT_COUNTRY
                          }
                        </option>
                        {taxationFormData.countryOption?.map((country) => (
                          <option
                            key={country?.taxResidencyResult?.taxResidency}
                            value={country?.taxResidentOtherName}
                          >
                            {country.taxResidentOtherName}
                          </option>
                        ))}
                      </select>
                      {taxationFormErrors.countryNameErrorList && (
                        <div className="[ invalid-feedback ] [ error-msg ]">
                          {taxationFormErrors.countryNameErrorList}
                        </div>
                      )}
                    </div>

                    <div className="[ mb-3 ]">
                      <label htmlFor="ssn" className="[ form-label ]">
                        {
                          STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                            .EDIT_TAXATION_DETAILS_MODAL.TAX_ID_LABEL
                        }
                      </label>
                      <input
                        id="ssn"
                        type="text"
                        name="ssn"
                        className={`[ form-control ] ${
                          taxationFormErrors.ssnErrorList
                            ? "[ is-invalid ]"
                            : ""
                        }`}
                        value={taxationFormData.ssn}
                        onChange={handleInputChange}
                        placeholder={
                          finInfo.useRegulatoryFieldsModel
                            ? STRINGS.BRANCH.MEMBER_PROFILE
                                .MEMBER_QUICK_VIEW_PANEL
                                .EDIT_TAXATION_DETAILS_MODAL
                                .TAX_ID_PLACEHOLDER_REG
                            : STRINGS.BRANCH.MEMBER_PROFILE
                                .MEMBER_QUICK_VIEW_PANEL
                                .EDIT_TAXATION_DETAILS_MODAL.TAX_ID_PLACEHOLDER
                        }
                        maxLength={finInfo.useRegulatoryFieldsModel ? 24 : 9}
                      />
                      {taxationFormErrors.ssnErrorList && (
                        <div className="[ invalid-feedback ] [ error-msg ]">
                          {taxationFormErrors.ssnErrorList}
                        </div>
                      )}
                    </div>

                    {taxationFormData.showOtherTaxResidencyField && (
                      <div className="[ mb-3 ]">
                        <label htmlFor="countryList" className="[ form-label ]">
                          {
                            STRINGS.BRANCH.MEMBER_PROFILE
                              .MEMBER_QUICK_VIEW_PANEL
                              .EDIT_TAXATION_DETAILS_MODAL.COUNTRY_LIST_LABEL
                          }
                        </label>
                        <input
                          id="countryList"
                          type="text"
                          name="countryList"
                          className={`[ form-control ] ${
                            taxationFormErrors.countryListErrorList
                              ? "[ is-invalid ]"
                              : ""
                          }`}
                          value={taxationFormData.countryList}
                          onChange={handleInputChange}
                          placeholder={
                            STRINGS.BRANCH.MEMBER_PROFILE
                              .MEMBER_QUICK_VIEW_PANEL
                              .EDIT_TAXATION_DETAILS_MODAL
                              .COUNTRY_LIST_PLACEHOLDER
                          }
                          maxLength={30}
                        />
                        {taxationFormErrors.countryListErrorList && (
                          <div className="[ invalid-feedback ] [ error-msg ]">
                            {taxationFormErrors.countryListErrorList}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="[ divider ]"></div>
      </Modal.Body>

      <Modal.Footer className="[ popup-modal-footer ] [ d-block ]">
        <div className="[ mb-3 ]">
          <button
            type="button"
            className="[ submit-btn btn ] [ w-100 ]"
            onClick={handleSave}
            style={{ borderRadius: ".5em" }}
          >
            {
              STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                .EDIT_TAXATION_DETAILS_MODAL.SAVE_BUTTON
            }
          </button>
        </div>
        <div className="[ mb-3 text-center ]">
          <span
            className="[ link ]"
            onClick={handleTaxationModalClose}
            role="presentation"
            style={{
              textDecoration: "underline",
              color: "#0090c6",
              cursor: "pointer",
            }}
          >
            {
              STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                .EDIT_TAXATION_DETAILS_MODAL.CANCEL_BUTTON
            }
          </span>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
